import React from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';

const Wishlist = () => {
  const { wishlistItems, removeFromWishlist, clearWishlist } = useWishlist();
  const { addToCart, isInCart } = useCart();

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    addToCart(product);
  };

  const handleRemoveFromWishlist = (productId, e) => {
    e.preventDefault();
    removeFromWishlist(productId);
  };

  if (wishlistItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <svg 
            className="w-20 h-20 text-gray-400 mx-auto mb-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
            />
          </svg>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Your wishlist is empty</h2>
          <p className="text-gray-600 mb-6">Save your favorite items to your wishlist and shop them later!</p>
          <Link 
            to="/"
            className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
          >
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">My Wishlist</h1>
        <button
          onClick={clearWishlist}
          className="text-red-600 hover:text-red-800 text-sm transition-colors"
        >
          Clear All
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {wishlistItems.map(item => (
          <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <Link to={`/product/${item.id}`}>
              <div className="relative">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
                {!item.inStock && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 text-xs rounded">
                    Out of Stock
                  </div>
                )}
                <button
                  onClick={(e) => handleRemoveFromWishlist(item.id, e)}
                  className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
                >
                  <svg 
                    className="w-5 h-5 text-red-500"
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
            </Link>

            <div className="p-4">
              <Link to={`/product/${item.id}`}>
                <h3 className="text-lg font-semibold text-gray-800 hover:text-blue-600 transition-colors">
                  {item.name}
                </h3>
              </Link>
              
              <p className="text-sm text-gray-600 mt-1">{item.category}</p>
              
              <div className="flex items-center mt-2">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg 
                      key={i}
                      className={`w-4 h-4 ${i < Math.floor(item.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300 fill-current'}`}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-gray-600 ml-2">({item.rating})</span>
              </div>

              <div className="flex items-center justify-between mt-4">
                <span className="text-xl font-bold text-blue-600">
                  ${item.price.toFixed(2)}
                </span>
                
                <button
                  onClick={(e) => handleAddToCart(item, e)}
                  disabled={!item.inStock || isInCart(item.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    !item.inStock 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isInCart(item.id)
                      ? 'bg-green-500 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {!item.inStock 
                    ? 'Out of Stock'
                    : isInCart(item.id)
                    ? 'In Cart'
                    : 'Add to Cart'
                  }
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <Link 
          to="/"
          className="text-blue-600 hover:text-blue-800 transition-colors"
        >
          Continue Shopping
        </Link>
      </div>
    </div>
  );
};

export default Wishlist;